import clsx from 'clsx';
import { useContext, createSignal, Show } from 'solid-js';
import { ColumnProps } from './column.d';
import { getBackgroundImageSrcData, ImageSrcData } from '../../components/image-component/image-component';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { PageContext } from '../../components/page/page-context-provider';
import { focusPosition } from '../../tools/get-image-focus';
import { ErrorCatcher } from '../../tools/error-catcher';
import { LockedContent } from '../../ui-components/layouts/locked-content';
import theme from '../../style/theme';

const { palette } = theme;

export const verticalAlignmentClasses: Record<string, string> = {
    top: 'justify-start',
    bottom: 'justify-end',
    center: 'justify-center',
    middle: 'justify-center',
    stretch: 'justify-stretch',
    spread: 'justify-between',
};

export const Column = (props: ColumnProps) => {
    const { imagesServiceUrl, supportedImageFormats, userState } = useContext(AppContext);
    const { withSidebarMenu } = useContext(PageContext);
    const [backgroundImageSrcData, setBackgroundImageSrcData] = createSignal<ImageSrcData>();

    if (props.image) {
        const backgroundImage: ImageSrcData = getBackgroundImageSrcData({
            imagesServiceUrl,
            supportedImageFormats,
            src: props.image.url,
            jwt: props.image.jwt,
            width: 960,
            height: 'AUTO',
        });

        setBackgroundImageSrcData(backgroundImage);
    }

    const isLockedContent = () => {
        return props.lockedContent;
    };

    const verticalAlignmentClass = () => props.verticalAlignment && verticalAlignmentClasses[props.verticalAlignment];

    const columnInlineStyle = () => {
        const declarations: string[] = [];

        const bg = props.backgroundValue;
        if (bg && bg !== 'transparent') {
            const colour = palette[bg as keyof typeof palette];
            if (colour) {
                declarations.push(`background-color: ${colour}`);
            }
        }

        const original = backgroundImageSrcData()?.original;
        if (original) {
            declarations.push(`background-image: url(${original})`);
            declarations.push('background-repeat: no-repeat');
            declarations.push('background-size: cover');
            declarations.push(`background-position: ${focusPosition(props?.image?.focus) || 'center'}`);
        }

        return declarations.length ? declarations.join('; ') : undefined;
    };

    const extendBgColorClasses = () => {
        if (!props.extendBgColor) {
            return 'column-dont-extend-bg';
        }

        if (!props.extendBgColorSide || props.extendBgColorSide !== 'right') {
            if (props.combineWithRightColumn) {
                return withSidebarMenu()
                    ? 'column-extend-left-with-sidebar-menu-and-combine-with-right-column column-extend-left-partial-style-with-sidebar-menu'
                    : 'column-extend-left-without-sidebar-menu-but-combine-with-right-column column-extend-left-partial-style-without-sidebar-menu';
            }

            return withSidebarMenu() ? 'column-extend-left-partial-style-with-sidebar-menu' : 'column-extend-left-partial-style-without-sidebar-menu';
        }
        else {
            return 'column-extend-right-style';
        }
    };

    return (
        <ErrorCatcher componentName="Column">
            <section
                class={clsx(
                    'atos-column',
                    'column-common',
                    {
                        'with-image': !!props.image,
                        'with-bg-color': !!props.backgroundValue,
                        /* Visible only on a tinted section; tailwind.css decides. */
                        'with-bg-white': props.backgroundValue === 'white',
                        /* Legacy attribute. Merges this column with the next only when
                           both draw a visible box; see `.combine-with-right` in tailwind.css. */
                        'combine-with-right': props.combineWithRightColumn,
                        'extend-bg-color-left': props.extendBgColor && props.extendBgColorSide !== 'right',
                        'extend-bg-color-right': props.extendBgColor && props.extendBgColorSide === 'right',
                        'no-extend': !props.extendBgColor,
                        'round-corners': props.roundCorners,
                        'with-divider': props.redDividerEnabled,
                        'column-round-corners': props.roundCorners,
                        'column-round-corners-if-extend-bg-color-right': props.extendBgColor && props.extendBgColorSide === 'right',
                        'column-round-corners-if-extend-bg-color-left':
                            props.extendBgColor &&
                            (props.extendBgColorSide === 'left' || props.extendBgColorSide === 'auto' || props.extendBgColorSide === undefined),

                        'column-paddings-if-background-value': props.backgroundValue && props.backgroundValue !== 'transparent',
                        /* Inset only where the column draws a visible edge. A white
                           column on a white page draws nothing, so insetting it would
                           push its text out of line with the plain column beside it. */
                        'column-visible-edge': props.withBorder
                            || (props.backgroundValue
                                && props.backgroundValue !== 'transparent'
                                && props.backgroundValue !== 'white'),
                        'column-border': props.withBorder && !props.extendBgColor && !props.combineWithRightColumn,
                    },
                    'column-grid-base-common',
                    'column-grid-items',
                    extendBgColorClasses(),

                    {
                        'column-red-divider-style-desktop': props.redDividerEnabled && !backgroundImageSrcData()?.original,
                        'column-desktop-large-css': props.backgroundValue !== 'transparent',
                        'column-tablet-css-bg-image column-mobile-css-bg-image': backgroundImageSrcData()?.original,
                        'column-tablet-css-bg-value column-mobile-css-bg-value ': props.backgroundValue && props.backgroundValue !== 'transparent',
                        'column-desktop-css-default column-tablet-css-default column-mobile-css-default': true,
                    }
                )}
                style={columnInlineStyle()}
            >
                <div
                    class={clsx(
                        'atos-column-flex [container-type:inline-size] h-auto flex flex-col',
                        {
                            'items-center': props.horizontalAlignment === 'center',
                            'ml-auto': props.horizontalAlignment === 'right',
                        },
                        verticalAlignmentClass()
                    )}
                    style={props.customCss}
                >
                    <Show when={backgroundImageSrcData()?.original}>
                        <img src={backgroundImageSrcData()?.original} alt={props.image?.altText} title={props.image?.title} class="opacity-0 absolute z-[-1]" />
                    </Show>
                    {props.children}
                </div>

                <Show when={isLockedContent() && !userState.user}>
                    <LockedContent hasHero={false} isColumn={true} text={props.lockedContentText}/>
                </Show>

            </section>
        </ErrorCatcher>
    );
};

Column.parseProps = (atts: any) => {
    return {
        horizontalAlignment: atts.horizontalAlignment,
        verticalAlignment: atts.verticalAlignment,
        backgroundValue: atts.backgroundColor ? atts.backgroundColor.name : undefined,
        roundCorners: atts.roundCorners,
        image: atts.image,
        extendBgColor: !!atts.backgroundColor && atts.extendBgColor,
        extendBgColorSide: !!atts.backgroundColor && atts.extendBgColorSide,
        combineWithRightColumn: atts.combineWithRight,
        redDividerEnabled: atts.redDividerEnabled,
        overlayType: atts.overlayType,
        lockedContent: atts.lockedContent,
        lockedContentText: atts.lockedContentText,
        withBorder: atts.withBorder,
    };
};
